/**
 * Service API pour la validation des calculs de paie
 */

import axios, { AxiosInstance } from 'axios';
import {
  ValidationResult,
  ValidationReport,
  ValidationConstraint,
  ConditionalValidationRequest,
  ConditionalValidationResult
} from '../types/validation';
import SecureLogger from '../utils/secureLogger.js';
import API_CONFIG from '../config/api.js';
import { getAuthorizedCompanyContext } from '../utils/companyContext.js';
import { showCompanySuspensionNotice } from '../utils/companySuspensionNotice.js';
import { installAxiosGlobalLoading } from '../utils/globalLoading.js';

class ValidationService {
  private axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: API_CONFIG.BASE_URL,
    });

    installAxiosGlobalLoading(this.axiosInstance);

    // Configurer l'intercepteur pour ajouter automatiquement le token
    this.axiosInstance.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          const companyContextId = getAuthorizedCompanyContext();
          if (companyContextId) {
            config.headers['X-Company-Id'] = String(companyContextId);
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 403) {
          showCompanySuspensionNotice(error.response.data);
        }
        return Promise.reject(error);
      }
    );

  }
  
  /**
   * Récupère les détails d'un calcul pour validation (sans le valider)
   */
  async getCalculationDetails(calculationId: number): Promise<any> {
    try {
      const response = await this.axiosInstance.get(
        `/api/hr/payroll-processing/calculations/${calculationId}/details`
      );
      return response.data;
    } catch (error) {
      SecureLogger.error('Failed to retrieve payroll calculation details');
      throw error;
    }
  }

  /**
   * Valide un calcul individuel
   */
  async validateCalculation(
    calculationId: number, 
    constraints?: ValidationConstraint[]
  ): Promise<ValidationResult> {
    try {
      const response = await this.axiosInstance.put(
        `/api/hr/payroll-processing/calculations/${calculationId}/validate`,
        { constraints }
      );
      return response.data;
    } catch (error) {
      SecureLogger.error('Failed to validate payroll calculation');
      throw error;
    }
  }

  /**
   * Annule la validation d'un calcul
   */
  async unvalidateCalculation(calculationId: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.axiosInstance.put(
        `/api/hr/payroll-processing/calculations/${calculationId}/unvalidate`
      );
      return response.data;
    } catch (error) {
      SecureLogger.error('Failed to cancel payroll validation');
      throw error;
    }
  }

  /**
   * Valide plusieurs calculs
   */
  async validateMultipleCalculations(
    calculationIds: number[],
    constraints?: ValidationConstraint[]
  ): Promise<ValidationResult[]> {
    try {
      const validationPromises = calculationIds.map(id => 
        this.validateCalculation(id, constraints)
      );
      return await Promise.all(validationPromises);
    } catch (error) {
      SecureLogger.error('Erreur lors de la validation multiple');
      throw error;
    }
  }

  /**
   * Validation conditionnelle basée sur des critères
   */
  async conditionalValidation(
    periodId: number,
    request: ConditionalValidationRequest
  ): Promise<ConditionalValidationResult> {
    try {
      const response = await this.axiosInstance.post(
        `/api/hr/payroll-processing/periods/${periodId}/calculations/conditional-validate`,
        request
      );
      return response.data;
    } catch (error) {
      SecureLogger.error('Erreur lors de la validation conditionnelle');
      throw error;
    }
  }

  /**
   * Génère un rapport de validation pour une période
   */
  async generateValidationReport(periodId: number): Promise<ValidationReport> {
    try {
      const response = await this.axiosInstance.get(
        `/api/hr/payroll-processing/periods/${periodId}/validation-report`
      );
      
      // Adapter la réponse du backend à l'interface TypeScript
      const backendData = response.data.data || response.data;
      
      return {
        period_id: backendData.period?.id || periodId,
        period_name: backendData.period?.name || 'Période inconnue',
        generated_at: backendData.generated_at || new Date().toISOString(),
        summary: {
          total_employees: backendData.summary?.total_employees,
          total_calculations: backendData.summary?.total_calculations || 0,
          valid_calculations: Number(backendData.summary?.validated_calculations ?? backendData.summary?.valid_calculations ?? 0),
          invalid_calculations: Math.max(0, Number(backendData.summary?.total_employees ?? backendData.summary?.total_calculations ?? 0) - Number(backendData.summary?.validated_calculations ?? backendData.summary?.valid_calculations ?? 0)),
          calculations_with_errors: backendData.summary?.error_calculations || backendData.calculations_with_errors?.length || 0,
          calculations_with_warnings: backendData.calculations_with_warnings?.length || 0,
          error_rate: backendData.summary?.total_calculations > 0 
            ? Math.round(((backendData.summary?.error_calculations || 0) / backendData.summary.total_calculations) * 100)
            : 0,
          validation_rate: backendData.summary?.validation_rate || 0
        },
        results: backendData.results || [],
        constraints_applied: backendData.constraints_applied || [],
        // Ajouter les données supplémentaires du backend
        calculations_with_errors: backendData.calculations_with_errors || [],
        calculations_with_warnings: backendData.calculations_with_warnings || [],
        department_breakdown: backendData.department_breakdown || [],
        position_breakdown: backendData.position_breakdown || []
      } as ValidationReport;
    } catch (error) {
      SecureLogger.error('Erreur lors de la génération du rapport');
      throw error;
    }
  }

  /**
   * Récupère les calculs d'une période avec leur statut de validation
   */
  async getPeriodCalculationsWithValidation(periodId: number): Promise<any[]> {
    try {
      const response = await this.axiosInstance.get(
        `/api/hr/payroll-processing/periods/${periodId}/calculations?include_validation=true`
      );
      return response.data;
    } catch (error) {
      SecureLogger.error('Erreur lors de la récupération des calculs');
      throw error;
    }
  }

  /**
   * Récupère les contraintes de validation par défaut
   */
  async getDefaultValidationConstraints(): Promise<ValidationConstraint[]> {
    try {
      const response = await this.axiosInstance.get(
        `/api/hr/payroll-processing/validation-constraints/default`
      );
      return response.data;
    } catch (error) {
      SecureLogger.error('Erreur lors de la récupération des contraintes');
      throw error;
    }
  }

  /**
   * Sauvegarde des contraintes de validation personnalisées
   */
  async saveCustomValidationConstraints(
    periodId: number,
    constraints: ValidationConstraint[]
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.axiosInstance.post(
        `/api/hr/payroll-processing/periods/${periodId}/validation-constraints`,
        { constraints }
      );
      return response.data;
    } catch (error) {
      SecureLogger.error('Erreur lors de la sauvegarde des contraintes');
      throw error;
    }
  }

  /**
   * Valide tous les calculs d'une période en lot
   */
  async validateAllPeriodCalculations(
    periodId: number,
    constraints?: ValidationConstraint[]
  ): Promise<ConditionalValidationResult> {
    try {
      const response = await this.axiosInstance.post(
        `/api/hr/payroll-processing/periods/${periodId}/validate-all`,
        { constraints }
      );
      return response.data;
    } catch (error) {
      SecureLogger.error('Erreur lors de la validation de tous les calculs');
      throw error;
    }
  }

  /**
   * Annule la validation de tous les calculs d'une période
   */
  async unvalidateAllPeriodCalculations(periodId: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.axiosInstance.post(
        `/api/hr/payroll-processing/periods/${periodId}/unvalidate-all`
      );
      return response.data;
    } catch (error) {
      SecureLogger.error('Erreur lors de l\'annulation de validation en lot');
      throw error;
    }
  }

  /**
   * Récupère les statistiques de validation pour une période
   */
  async getValidationStatistics(periodId: number): Promise<{
    total_calculations: number;
    validated_calculations: number;
    pending_validation: number;
    validation_errors: number;
    validation_warnings: number;
  }> {
    try {
      const response = await this.axiosInstance.get(
        `/api/hr/payroll-processing/periods/${periodId}/validation-stats`
      );
      return response.data;
    } catch (error) {
      SecureLogger.error('Erreur lors de la récupération des statistiques');
      throw error;
    }
  }
}

export default new ValidationService();
